import { useCallback, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { GameMessage, PlayerId } from "../types";
import { GameStatus } from "../types/game-status";
import { updatePlayer } from "../redux/slices/player";
import { updateGame } from "../redux/slices/game";
import { setServerTimeOffset } from "../time";
import { useGameConnection } from "./useGameConnection";
import { selectGameStatus } from "../redux/selectors";
import { updateError } from "../redux/slices/meta";
import { useSmartNavigate } from "./useSmartNavigate";
import { clearRoom } from "../redux/slices/menu";

export function useGameManager(playerId: PlayerId) {
  const dispatch = useAppDispatch();
  const navigate = useSmartNavigate();
  const [shouldConnect, setShouldConnect] = useState(true);
  const gameStatus = useAppSelector(selectGameStatus);
  const previousGameStatusRef = useRef<GameStatus>(gameStatus);

  const goAway = useCallback(() => {
    setShouldConnect(false);
    dispatch(clearRoom());
    navigate("/", { replace: true });
  }, [dispatch, navigate]);

  const disconnect = useCallback(() => {
    setShouldConnect(false);
  }, []);

  const handleMessage = useCallback(
    (gameMessage: GameMessage) => {
      if (gameMessage.goaway) {
        goAway();
        return;
      }

      if (gameMessage.now) {
        setServerTimeOffset(gameMessage.now);
      }

      if (gameMessage.error) {
        dispatch(updateError(gameMessage.error));
      } else {
        // Clear error if we got an ack OR if game status changed
        const gameStatusChanged =
          gameMessage.game &&
          gameMessage.game.status_str !== previousGameStatusRef.current;

        if (gameMessage.ack || gameStatusChanged) {
          dispatch(updateError(""));
        }
      }

      if (gameMessage.game) {
        if (gameMessage.game.status_str === GameStatus.GameStatusInvalid) {
          // Game was closed/invalidated - navigate away
          goAway();
          return;
        }

        if (gameMessage.game.status_str === GameStatus.GameStatusResults) {
          setShouldConnect(false); // Disconnect on game end
        }

        dispatch(updateGame(gameMessage.game));

        // Update our reference for next comparison
        previousGameStatusRef.current = gameMessage.game.status_str;
      }

      if (gameMessage.player) {
        if (gameMessage.player.status_str === GameStatus.GameStatusInvalid) {
          goAway();
        } else {
          dispatch(updatePlayer(gameMessage.player));
        }
      }
    },
    [dispatch, goAway]
  );

  const handleConnectionClose = useCallback(() => {
    // Only disable connection for specific game states or user-initiated actions
    // Don't disable for network issues to allow automatic reconnection
    if (
      gameStatus === GameStatus.GameStatusResults ||
      gameStatus === GameStatus.GameStatusInvalid
    ) {
      setShouldConnect(false);
    }
    // For other cases, let the WebSocket handle reconnection automatically
  }, [gameStatus]);

  const { sendMessage } = useGameConnection({
    playerId,
    onMessage: handleMessage,
    onClose: handleConnectionClose,
    enabled: shouldConnect,
  });

  return { sendMessage, disconnect };
}
